import { t } from 'ttag';

/**
 * Supplies the support page FAQ covering device battery life, charging, electrodes, event notification calls, report turnaround, study type changes and RMA handling. Returns a fixed list of question and answer pairs, each wrapped in the gettext t template so the text is translated when the list is built.
 *
 * @returns Ordered list of translated FAQ question and answer pairs.
 */
const getFaqData = () => [
  {
    question: t`How long does the battery last?`,
    answer: t`Under typical conditions, the battery should last at least 24 hours. If you are in a poor cell coverage area, the battery may last up to 12 hours and will need to be charged more often.`,
  },
  {
    question: t`How many times does the patient have to charge the device per day?`,
    answer: t`Typically, the device will need to be charged daily for 1 hour. If the device is in a poor cell coverage area, it will need to be charged twice a day, for 1 hour at a time.`,
  },
  {
    question: t`How often does the patient need to change the electrodes?`,
    answer: t`Electrodes should be changed daily. Please see the documentation provided with the electrodes for the recommended wear time.`,
  },
  {
    question: t`If the patient has a major event, who is notified via phone?`,
    answer: t`If a major event occurs during office hours, Biotricity will call the clinic to report the event and may notify the clinician in lieu of the doctor. If the event occurs after office hours, Biotricity will report the event directly to the after-hours phone number provided.`,
  },
  {
    question: t`Does the report download before the patient brings the device back?`,
    answer: t`Yes, in most cases the report will be provided before the patient brings the device back. Once the study is complete, we will create, review, and provide the report to the clinic within 3 business days.  In the event that the patient turned off the device before the study was complete, or the device is in a low cell coverage area and cannot communicate with Biotricity, we may not be able to start the report until the device is returned to the clinic and the data is uploaded.`,
  },
  {
    question: t`What happens if a device breaks during the study?`,
    answer: t`When the clinic or a patient contacts us we will attempt to troubleshoot the issue. If a replacement is needed, the patient should return the device to his/her clinic and resume the study with a replacement device. The clinic may then open a support request for an RMA.`,
  },
  {
    question: t`When should the patient press the event button?`,
    answer: t`The patient should press the event button anytime they experience a symptom(s).`,
  },
  {
    question: t`Are all of your call center technicians and ECG technicians fluent in English?`,
    answer: t`Yes, all of our call center and ECG technicians are CCT-certified, U.S. based, and fluent in English.`,
  },
  {
    question: t`Can we change the study type (e.g. from Event to MCT) without requiring the patient to bring the device back to the physician’s office?`,
    answer: t`Yes, a request can be made to Biotricity by phone or email to change the study type during an ongoing study. The patient will not be required to bring the device back to the physician in order for the study type to be changed.`,
  },
  {
    question: t`Do you provide electrodes?`,
    answer: t`No, we do not provide electrodes.`,
  },
  {
    question: t`Can a physician view the live ECG at any time? Can we see real-time telemetry on our patients?`,
    answer: t`No, a physician can not look at the incoming live ECG stream at any time. We are currently working to enable this functionality.`,
  },
  {
    question: t`Can we view the portal anywhere, anytime?`,
    answer: t`Yes, all that is needed to view the portal is an internet connection with a browser.`,
  },
  {
    question: t`How do I get notified when my patients experience events?`,
    answer: t`One of our technicians will call to notify for events. The notification event report will also be available in your portal’s inbox.`,
  },
  {
    question: t`How can we make changes or add information once a study has been activated?`,
    answer: t`Once a study has been activated, on the study management page information can be edited. Please refer to the Features Manual in the Edit Study Details section.`,
  },
  {
    question: t`When will the reports be ready so we can schedule follow-up appointments with patients?`,
    answer: t`Reports will be ready within 3 business days after the study has been completed. If the patient turns off the device before the study is complete or if the device is in a low cell coverage area, the report will not be able to be created until the device is returned and allowed to upload the data.`,
  },
  {
    question: t`Can we generate an end-of-use report on our own if we stop the study prematurely?`,
    answer: t`Once the study has been stopped, we will create the end-of-use report and make it available within 3 business days. You will not be able to create an end-of-use report on your own.`,
  },
  {
    question: t`Can we order supplies and accessories through the portal?`,
    answer: t`Not yet, but we are working on this functionality.`,
  },
  {
    question: t`What happens if a device breaks during a study?`,
    answer: t`When the clinic or a patient contacts us we will attempt to troubleshoot the issue. If a replacement is needed the patient should return the device to his/her clinic and start a another study with a replacement device. The clinic may then open a support request or RMA.`,
  },
];

export default getFaqData;
